/**
 * About Us — Icon Boxes — frontend behaviour.
 *
 * Loaded as an ES module ONLY on pages where this block renders. Imports its
 * own CSS so the section ships as one isolated bundle.
 *
 * Motion (mirrors the Storyline block's left column, no axis):
 *   - LEFT column: entrance blur-in (eyebrow → title → box) + soft-green bar
 *     sweep, fires once when the section nears the viewport.
 *   - RIGHT cards: each card reveals independently as it hits 82% of the
 *     viewport — blur-up + rise, icon pops with a back-out ease, then the
 *     inner bits (title, text, bullets, badge) stagger in.
 *   - prefers-reduced-motion pins everything to its final visible state.
 */
import './style.scss';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

function init() {
	const sections = document.querySelectorAll('[data-aboxes]');
	if (!sections.length) return;

	const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

	sections.forEach((section) => {
		const headEls = Array.from(section.querySelectorAll('[data-aboxes-anim]'));
		const cards = Array.from(section.querySelectorAll('[data-aboxes-card]'));
		const softBar = section.querySelector('.gv-aboxes__box-bar');

		if (reduced) {
			[...headEls, ...cards].forEach((el) => {
				el.style.opacity = '1';
				el.style.filter = 'none';
				el.style.transform = 'none';
			});
			return;
		}

		// --- initial hidden states -----------------------------------
		gsap.set(headEls, { opacity: 0, y: 20, filter: 'blur(10px)' });
		if (softBar) gsap.set(softBar, { scaleX: 0, transformOrigin: 'left center' });

		cards.forEach((card) => {
			gsap.set(card, { opacity: 0, y: 28, filter: 'blur(12px)' });
			const icon = card.querySelector('.gv-aboxes__card-icon');
			if (icon) gsap.set(icon, { scale: 0, transformOrigin: 'center' });
			const inner = card.querySelectorAll(
				'.gv-aboxes__card-title, .gv-aboxes__card-text, .gv-aboxes__bullet, .gv-aboxes__card-badge'
			);
			gsap.set(inner, { opacity: 0, y: 14, filter: 'blur(6px)' });
		});

		// --- LEFT entrance — once on enter ---------------------------
		const ioHead = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (!entry.isIntersecting) return;
					ioHead.unobserve(entry.target);
					const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });
					headEls.forEach((el, i) => {
						tl.to(el, { opacity: 1, y: 0, filter: 'blur(0px)', duration: 0.7, clearProps: 'transform' }, 0.1 + i * 0.12);
					});
					if (softBar) {
						tl.to(softBar, { scaleX: 1, duration: 0.7, ease: 'power2.out' }, '>-0.2');
					}
				});
			},
			{ rootMargin: '0px 0px -15% 0px', threshold: 0.1 }
		);
		ioHead.observe(section);

		// --- RIGHT cards — per-card exploratory entrance -------------
		cards.forEach((card) => {
			const icon = card.querySelector('.gv-aboxes__card-icon');
			const inner = card.querySelectorAll(
				'.gv-aboxes__card-title, .gv-aboxes__card-text, .gv-aboxes__bullet, .gv-aboxes__card-badge'
			);

			ScrollTrigger.create({
				trigger: card,
				start: 'top 82%',
				once: true,
				onEnter: () => {
					const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });

					tl.to(card, {
						opacity: 1,
						y: 0,
						filter: 'blur(0px)',
						duration: 0.7,
						// Clear the leftover inline transform so the CSS :hover
						// lift (translateY) isn't pinned by GSAP.
						clearProps: 'transform,filter',
					});

					if (icon) {
						tl.to(icon, { scale: 1, duration: 0.55, ease: 'back.out(2.2)', clearProps: 'transform' }, '<0.1');
					}

					tl.to(inner, {
						opacity: 1,
						y: 0,
						filter: 'blur(0px)',
						duration: 0.5,
						stagger: 0.08,
						clearProps: 'transform,filter',
					}, '<0.15');
				},
			});
		});
	});
}

if (document.readyState !== 'loading') init();
else document.addEventListener('DOMContentLoaded', init);
